import React from 'react';
import { getResourceColor, getInitials } from '../../../../utils/resourceUtils';
import { getStatus, formatLicenses, getReplacementName } from '../employeeUtils';
import ActionMenu from '../../../../components/shared/ActionMenu/ActionMenu';

/**
 * EmployeeTable Component - Renders the employee list as a table.
 */
export function EmployeeTable({ 
    employees, 
    absences, 
    sortConfig, 
    onSort, 
    onEdit, 
    onDelete, 
    onManageAbsence,
    selectedIds = [],
    setSelectedIds
}) {
    return (
        <div className="employee-table-container">
            <table className="employee-table">
                <thead>
                    <tr>
                        <th>
                            <input 
                                type="checkbox" 
                                checked={selectedIds.length === employees.length && employees.length > 0}
                                onChange={(e) => {
                                    if (e.target.checked) {
                                        setSelectedIds(employees.map(emp => emp.id));
                                    } else {
                                        setSelectedIds([]);
                                    }
                                }}
                            />
                        </th>
                        <th onClick={() => onSort('name')} className={`sort-header col-name ${sortConfig.key === 'name' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Mitarbeiter</span>
                                <span className="sort-indicator">{sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        
                        {/* NEW: Status Column */}
                        <th onClick={() => onSort('status')} className={`sort-header col-center ${sortConfig.key === 'status' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Status</span>
                                <span className="sort-indicator">{sortConfig.key === 'status' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>

                        <th onClick={() => onSort('role')} className={`sort-header col-center ${sortConfig.key === 'role' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Rolle</span>
                                <span className="sort-indicator">{sortConfig.key === 'role' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th onClick={() => onSort('license')} className={`sort-header col-center ${sortConfig.key === 'license' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Führerschein</span>
                                <span className="sort-indicator">{sortConfig.key === 'license' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th onClick={() => onSort('replacement')} className={`sort-header col-center ${sortConfig.key === 'replacement' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Vertretung</span>
                                <span className="sort-indicator">{sortConfig.key === 'replacement' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th onClick={() => onSort('contact')} className={`sort-header col-center ${sortConfig.key === 'contact' ? 'active' : ''}`}>
                            <div className="th-content">
                                <span>Kontakt</span>
                                <span className="sort-indicator">{sortConfig.key === 'contact' ? (sortConfig.direction === 'asc' ? '↑' : '↓') : '↕'}</span>
                            </div>
                        </th>
                        <th style={{ textAlign: 'center' }}>Aktionen</th>
                    </tr>
                </thead>
                <tbody>
                    {employees.map(emp => {
                        const status = getStatus(emp.id, absences);
                        const replacementName = getReplacementName(emp.vacation_replacement_id, employees);
                        
                        const actions = [
                            {
                                icon: (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                        <line x1="16" y1="2" x2="16" y2="6" />
                                        <line x1="8" y1="2" x2="8" y2="6" />
                                        <line x1="3" y1="10" x2="21" y2="10" />
                                    </svg>
                                ),
                                label: 'Abwesenheiten',
                                onClick: () => onManageAbsence(emp.id),
                                tooltip: 'Abwesenheiten'
                            },
                            {
                                icon: (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                                        <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                                    </svg>
                                ),
                                label: 'Bearbeiten',
                                onClick: () => onEdit(emp),
                                tooltip: 'Bearbeiten'
                            },
                            !emp.user_id && {
                                icon: (
                                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                        <polyline points="3 6 5 6 21 6" />
                                        <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                                        <line x1="10" y1="11" x2="10" y2="17" />
                                        <line x1="14" y1="11" x2="14" y2="17" />
                                    </svg>
                                ),
                                label: 'Löschen',
                                variant: 'delete',
                                onClick: () => onDelete(emp.id),
                                tooltip: 'Löschen'
                            }
                        ].filter(Boolean);

                        return (
                            <tr key={emp.id} className="clickable-row" onClick={() => onEdit(emp)}>
                                <td onClick={(e) => e.stopPropagation()}>
                                    <input 
                                        type="checkbox" 
                                        checked={selectedIds.includes(emp.id)}
                                        onChange={(e) => {
                                            if (e.target.checked) {
                                                setSelectedIds([...selectedIds, emp.id]);
                                            } else {
                                                setSelectedIds(selectedIds.filter(id => id !== emp.id));
                                            }
                                        }}
                                    />
                                </td>
                                <td className="col-name">
                                    <div className="employee-table-name-cell">
                                        <div className="employee-table-avatar" style={{ background: getResourceColor(emp) }}>
                                            {getInitials(emp.name)}
                                        </div>
                                        <div className="employee-table-name">{emp.name}</div>
                                    </div>
                                </td>
                                
                                {/* NEW: Status Badge */}
                                <td className="col-center">
                                    <div className={`employee-table-badge ${status.badge}`}>
                                        <span className="status-dot-mini" style={{ background: status.color }}></span>
                                        {status.label}
                                    </div>
                                </td>

                                <td className="col-center">
                                    <div className="employee-table-role">{emp.category_name || '—'}</div>
                                </td>
                                <td className="col-center">{formatLicenses(emp)}</td>
                                <td className="col-center">{replacementName}</td>
                                <td className="employee-table-contact col-center">
                                    <div className="contact-info-stack">
                                        {emp.email && <div className="contact-email">{emp.email}</div>}
                                        {emp.phone && <div className="contact-phone">{emp.phone}</div>}
                                        {emp.emergency_contact_phone && (
                                            <div className="contact-emergency" title={`Notfall: ${emp.emergency_contact_name}`}>
                                                🚨 {emp.emergency_contact_phone}
                                            </div>
                                        )}
                                        {(!emp.email && !emp.phone && !emp.emergency_contact_phone) && '—'}
                                    </div>
                                </td>
                                <td>
                                    <ActionMenu actions={actions} />
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            </table>
        </div>
    );
}
